// Explicit extension: the engine's test suite imports this file with plain Node,
// which does not do Vite's extensionless resolution.
import { ROUND_SEED_SEPARATOR } from './crashFairness.js'

// The block a player copies to have a round re-derived somewhere else — an
// assistant, a REPL, a spreadsheet.
//
// The bar it has to clear: someone holding only this text, with no access to the
// game or its API, must be able to arrive at the recorded multiplier. Everything
// that serves that is in; everything else is out. A block nobody reads because it
// is three screens long verifies nothing.
//
// Three things look like padding and are not:
//
//   - the encoding line. These are hex STRINGS that get hashed as their UTF-8
//     bytes. Left to guess, a verifier hex-decodes them first, computes a
//     different number, and reports a mismatch that does not exist.
//   - both multiplier forms. The engine stores an unrounded float; that is what a
//     recomputation lands on. The player saw 2dp. Publish one and the other reads
//     as a discrepancy.
//   - the three client seeds, on a v2 round. They are INPUTS to the round seed, so
//     a block without them is not verifiable at all — the derivation line cannot
//     be computed. Their slot order is the hash order, which is why they are
//     numbered rather than listed as a set.
//
// It does not ask anyone to certify the game honest — the arithmetic only decides
// whether the published values are internally consistent with each other.
export const buildVerificationReport = (round, gameCode) => {
  const separator = round?.raw?.separator ?? ROUND_SEED_SEPARATOR
  const houseEdge = round?.raw?.houseEdge ?? '—'
  const maxRaw = round?.raw?.maxRawOutcome ?? '—'
  const cap = round?.raw?.maxMultiplierCap ?? 0

  // Read off the round, never inferred from whether the seeds are present — a v2
  // round whose seeds did not load must not silently publish a v1 derivation line
  // that cannot reproduce its own result.
  const version = Number(round?.provablyFairVersion ?? 1)
  const clientSeeds = round?.clientSeeds ?? null
  const isV2 = version === 2 && Boolean(clientSeeds?.length)

  // Slot order IS hash order. Numbered and one per line so the reader
  // reconstructs the concatenation without being told the convention, and
  // annotated so a server-filled slot is never mistaken for a player's.
  const seedLines = isV2
    ? clientSeeds.map(
        (entry) =>
          `clientSeed${entry.slot}    = ${entry.clientSeed}   (${
            entry.source === 'SERVER_FALLBACK' ? 'server fallback' : 'player'
          })`
      )
    : []

  const derivationLine = isV2
    ? `  roundSeed      = SHA256(serverSeed + "${separator}" + roundId + "${separator}" + clientSeed1 + "${separator}" + clientSeed2 + "${separator}" + clientSeed3)`
    : `  roundSeed      = SHA256(serverSeed + "${separator}" + roundId)`

  return [
    `${gameCode ?? 'Crash'} — verify round ${round?.roundId ?? '—'}`,
    `algorithm version ${version}`,
    '',
    `serverSeed     = ${round?.serverSeed ?? 'not published yet'}`,
    `serverSeedHash = ${round?.serverSeedHash ?? '—'}`,
    ...seedLines,
    `roundSeed      = ${round?.hash ?? '—'}`,
    `gameSeed       = ${round?.seed ?? '—'}`,
    `roundId        = ${round?.roundId ?? '—'}`,
    `result         = ${round?.crashPoint ?? '—'} (stored ${round?.crashMultiplier ?? '—'})`,
    '',
    'How the crash point is made:',
    '  serverSeedHash = SHA256(serverSeed)          <- published before the round',
    derivationLine,
    '  digest         = HMAC_SHA256(key = roundSeed, msg = gameSeed)',
    '  raw            = first 4 bytes of digest, big-endian uint32',
    `  result         = (${maxRaw} / (raw + 1)) * (1 - ${houseEdge})`,
    cap
      ? `  then floor at 1.00, cap at ${cap}, round to 2 decimals`
      : '  then floor at 1.00, round to 2 decimals',
    '',
    'Every value is a lowercase hex string. Hash the UTF-8 bytes of the string itself,',
    'not the bytes it decodes to.',
    '',
    // Written as the player's own question, since this text lands in a chat box.
    'Please work through each step and tell me whether the result is correct.',
  ].join('\n')
}

export default buildVerificationReport
